import { useField } from 'formik'
import { useBrandKits } from 'hooks/queries/brandKits/useBrandKits'
import { MenuTrigger } from 'components/MenuTrigger/MenuTrigger'
import { Button } from 'blaze-ui/Button'
import { Menu } from 'components/Menu/Menu'
import { SelectableItem } from 'components/Menu/MenuItem'
import { Key } from 'react'
import { BrandKitLogo } from 'screens/BrandKit/BrandKitLogo/BrandKitLogo'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import Styles from './BrandKitField.module.scss'
import { ButtonProps } from 'blaze-ui/Button/Types'
import { Brand } from 'blaze-ui/icons'

import { Item, Section } from '@react-stately/collections'
import { ItemElement } from '@react-types/shared'

import { useOpenBrandKitCreationModal } from 'hooks/useOpenBrandKitCreationModal'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { Text } from 'blaze-ui/Text/Text'
import cx from 'classnames'

interface BaseBrandKitFieldProps extends Pick<ButtonProps, 'size'> {
  value?: number
  onChange?: (brandKitId?: number) => void
  isDisabled?: boolean
  allowCreate?: boolean
}

export const BaseBrandKitField = ({
  size = 'lg',
  value,
  onChange,
  isDisabled,
  allowCreate = true,
}: BaseBrandKitFieldProps) => {
  const { isMobile } = useResponsiveDesign()

  const { data: brandKits, isLoading } = useBrandKits()
  const selectedBrand = brandKits?.find((brandKit) => brandKit.id === value)

  const { openBrandKitCreationModal } = useOpenBrandKitCreationModal({ newStack: true })

  const handleAction = (key: Key) => {
    if (key === 'create') {
      openBrandKitCreationModal()
      return
    }

    const brandKitId = parseInt(key as string)
    if (value === brandKitId) {
      onChange?.(undefined)
    } else {
      onChange?.(brandKitId)
    }
  }

  if (isLoading) {
    return (
      <Button variant="secondary" size={size} fullWidth={isMobile} isDisabled>
        ...
      </Button>
    )
  }

  if (brandKits?.length === 0) {
    return (
      <Button
        isDisabled={isDisabled}
        variant="secondary"
        size={size}
        fullWidth={isMobile}
        frontIcon={Brand}
        onPress={() => openBrandKitCreationModal()}
      >
        Create New Brand Kit
      </Button>
    )
  }

  if (brandKits?.length === 1 && !allowCreate) {
    const brandKit = brandKits[0]
    return (
      <div className={cx(Styles.brandKitItem, Styles.static)}>
        <BrandKitLogo brandKit={brandKit} size={20} />
        <Text lineClamp={1} variant="smallList" color="var(--dark-80)">
          {brandKit.name}
        </Text>
      </div>
    )
  }

  return (
    <MenuTrigger popoverClassName={Styles.popover}>
      <Button
        variant="secondary"
        withChevron="down"
        frontIcon={selectedBrand ? () => <BrandKitLogo brandKit={selectedBrand} size={20} /> : undefined}
        size={size}
        fullWidth={isMobile}
        isDisabled={isDisabled}
      >
        <span className={Styles.buttonText}>{selectedBrand?.name || 'Select Brand Kit'}</span>
      </Button>
      <Menu
        onAction={handleAction}
        aria-label="Brand Kit"
        showCheckmark
        selectionMode="multiple"
        selectedKeys={[selectedBrand?.id?.toString() as Key]}
        itemClassName={Styles.brandKitItem}
      >
        <Section>
          {
            brandKits?.map((brandKit) => (
              <SelectableItem type="item" key={brandKit.id} textValue={brandKit.name}>
                <BrandKitLogo brandKit={brandKit} size={20} />
                <span className={Styles.name}>{brandKit.name}</span>
              </SelectableItem>
            )) as ItemElement<HTMLElement>[]
          }
        </Section>
        {allowCreate && (
          <Section>
            <Item key="create" textValue="Create New Brand Kit">
              Create New Brand Kit
            </Item>
          </Section>
        )}
      </Menu>
    </MenuTrigger>
  )
}

interface BrandKitFieldProps extends Pick<ButtonProps, 'size'> {
  onChange?: (brandKitId?: number) => void
  isDisabled?: boolean
}

export const BrandKitField = ({ size = 'lg', onChange, isDisabled }: BrandKitFieldProps) => {
  const [field, , helpers] = useField('brand_kit_id')
  const isVirtualMarketer = useCurrentWorkspace()?.is_virtual_marketer

  const handleChange = (brandKitId?: number) => {
    helpers.setValue(brandKitId)
    onChange?.(brandKitId)
  }

  return (
    <BaseBrandKitField
      size={size}
      value={field.value}
      onChange={handleChange}
      isDisabled={isDisabled}
      allowCreate={!isVirtualMarketer}
    />
  )
}
